import { memo, useState } from 'react';
import RangeIndicator from './RangeIndicator';
import { 
  getCommunicationFrequencyLevel, 
  getCommunicationStyleLevel, 
  getStrategicInfluenceLevel, 
  getResultVisibilityLevel,
  getQualityControlLevel,
  getQualityControlDescription,
  LEVEL_LABELS 
} from '../utils/compensationPartner/levelMapper';


function PositionDetails({ position }) {
  const [isParametersOpen, setIsParametersOpen] = useState(false);
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  
  // State for source inclusion in recommendation calculation
  const [sourceInclusion, setSourceInclusion] = useState({
    compensationPartner: true,
    kununu: true,
    gehaltDe: true
  });
  
  if (!position) return null;
  
  // Extract stellen data from compensationPartner metadata with proper validation
  const stellenData = position?.compensationPartner?.metadata?.stellen || [];
  
  // Calculate adjustments for each source with null safety - using Total Cash for all comparisons
  const currentMonthly = position?.salary?.peerigonCurrentTotalCash ? Math.round(position.salary.peerigonCurrentTotalCash / 12) : 0;
  const cpMonthly = position?.salary?.medianTotalCash ? Math.round(position.salary.medianTotalCash / 12) : 0;
  const kununuMonthly = position?.kununu?.averageSalary ? Math.round(position.kununu.averageSalary / 12) : null;
  const gehaltDeMonthly = position?.gehaltDe?.averageSalary ? Math.round(position.gehaltDe.averageSalary / 12) : null;
  
  // Check if this position has management responsibility
  const hasManagementResponsibility = position?.positionGroupId === "Mit Personalverantwortung" || 
                                      position?.teamgroesseId || 
                                      position?.compensationPartner?.config?.hasManagement;
  
  // Calculate how much more/less the market sources pay compared to Peerigon
  const cpAdjustment = cpMonthly - currentMonthly;
  const kununuAdjustment = kununuMonthly ? kununuMonthly - currentMonthly : null;
  const gehaltDeAdjustment = gehaltDeMonthly ? gehaltDeMonthly - currentMonthly : null;
  
  // Determine recommendation - calculate average from enabled sources only
  const allAdjustments = [
    sourceInclusion.compensationPartner && { source: 'Compensation Partner', amount: cpAdjustment, priority: 1, key: 'compensationPartner' },
    sourceInclusion.kununu && kununuAdjustment !== null && { source: 'Kununu', amount: kununuAdjustment, priority: 2, key: 'kununu' },
    sourceInclusion.gehaltDe && gehaltDeAdjustment !== null && { source: 'Gehalt.de', amount: gehaltDeAdjustment, priority: 3, key: 'gehaltDe' }
  ].filter(Boolean);
  
  // All available adjustments (for display purposes, regardless of inclusion state)
  const allAvailableAdjustments = [
    { source: 'Compensation Partner', amount: cpAdjustment, priority: 1, key: 'compensationPartner', available: true },
    kununuAdjustment !== null && { source: 'Kununu', amount: kununuAdjustment, priority: 2, key: 'kununu', available: true },
    gehaltDeAdjustment !== null && { source: 'Gehalt.de', amount: gehaltDeAdjustment, priority: 3, key: 'gehaltDe', available: true }
  ].filter(Boolean);
  
  // For display: only show positive adjustments
  const positiveAdjustments = allAdjustments.filter(a => a.amount > 0);
  
  // For calculation: use enabled adjustments (including negative ones) for the average
  const averageAdjustment = allAdjustments.length > 0 
    ? Math.round(allAdjustments.reduce((sum, a) => sum + a.amount, 0) / allAdjustments.length)
    : 0;
  
  // Handler for checkbox changes
  const handleSourceToggle = (sourceKey) => {
    setSourceInclusion(prev => ({
      ...prev,
      [sourceKey]: !prev[sourceKey]
    }));
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-white">
        <h3 className="text-xl font-bold">{position.positionsBezeichnung}</h3>
        <div className="flex items-center mt-2 space-x-4 text-blue-100">
          <span>📍 {position.department}</span>
          <div className="flex items-center gap-2">
            <span>💰</span>
            <div className="flex flex-col">
              <span className="text-white font-semibold">{position.salary?.peerigonCurrentTotalCash?.toLocaleString('de-DE')} € / Jahr</span>
              <span className="text-xs text-blue-200">{currentMonthly.toLocaleString('de-DE')} € / Monat</span>
            </div>
          </div>
          <span>📚 {position.mindesterfahrungId}</span>
        </div>
      </div>
      
      {/* Collapsible Position Parameters from Compensation Partner */}
      <div className="border-b border-gray-200">
        <button 
          onClick={() => setIsParametersOpen(!isParametersOpen)}
          className="w-full p-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200 flex items-center justify-between text-left"
        >
          <h4 className="text-lg font-semibold">📋 Positions-Parameter (Compensation Partner)</h4>
          <svg 
            className={`w-5 h-5 transition-transform duration-200 ${isParametersOpen ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isParametersOpen && (
          <div className="p-6 bg-gray-50">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Basic Info Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <span className="text-xl mr-2">📋</span>
                  <h4 className="font-semibold text-gray-900">Grunddaten</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ausbildung (Minimum):</span>
                    <span className="font-medium">{position.mindestausbildungId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Erfahrung:</span>
                    <span className="font-medium">{position.mindesterfahrungId}</span>
                  </div>
                </div>
              </div>
              
              {/* Responsibility Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <span className="text-xl mr-2">🎯</span>
                  <h4 className="font-semibold text-gray-900">Verantwortung</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Personalverantwortung:</span>
                    <span className="font-medium">{position.positionGroupId}</span>
                  </div>
                  {position.teamgroesseId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Teamgröße:</span>
                      <span className="font-medium">{position.teamgroesseId}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Budget:</span>
                    {position.budget === "Ja" ? (
                      <span className="text-green-600 font-medium">✓ {position.budgetVformat || 'Ja'}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Umsatz:</span>
                    {position.umsatz === "Ja" ? (
                      <span className="text-green-600 font-medium">✓ {position.umsatzVformat || 'Ja'}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Communication Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <span className="text-xl mr-2">💬</span>
                  <h4 className="font-semibold text-gray-900">Kommunikation</h4>
                  {position.hasGeneratedDefaults && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      Geschätzt
                    </span>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Umfang:</span>
                      <span className="text-sm font-medium text-blue-600">
                        {LEVEL_LABELS.communication.frequency[getCommunicationFrequencyLevel(position.kommunikation)]}
                      </span>
                    </div>
                    <RangeIndicator level={getCommunicationFrequencyLevel(position.kommunikation) + 1} maxLevel={4} labels={LEVEL_LABELS.communication.frequency} />
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Art:</span>
                      <span className="text-sm font-medium text-blue-600">
                        {LEVEL_LABELS.communication.style[getCommunicationStyleLevel(position.kommunikation)]}
                      </span>
                    </div>
                    <RangeIndicator level={getCommunicationStyleLevel(position.kommunikation) + 1} maxLevel={3} labels={LEVEL_LABELS.communication.style} />
                  </div>
                </div>
              </div>
              
              {/* Complexity Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <span className="text-xl mr-2">🧠</span>
                  <h4 className="font-semibold text-gray-900">Komplexität</h4>
                  {position.hasGeneratedDefaults && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      Geschätzt
                    </span>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Strategischer Einfluss:</span>
                      <span className="text-sm font-medium text-blue-600">
                        {LEVEL_LABELS.complexity.strategic[getStrategicInfluenceLevel(position.komplexität)]}
                      </span>
                    </div>
                    <RangeIndicator level={getStrategicInfluenceLevel(position.komplexität) + 1} maxLevel={6} labels={LEVEL_LABELS.complexity.strategic} />
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Ergebnisse sichtbar:</span>
                      <span className="text-sm font-medium text-blue-600">
                        {LEVEL_LABELS.complexity.visibility[getResultVisibilityLevel(position.komplexität)]}
                      </span>
                    </div>
                    <RangeIndicator level={getResultVisibilityLevel(position.komplexität) + 1} maxLevel={6} labels={LEVEL_LABELS.complexity.visibility} />
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Qualitätskontrolle:</span>
                      <span className="text-sm font-medium text-blue-600">
                        {LEVEL_LABELS.complexity.qualityControl[getQualityControlLevel(position.komplexität)]}
                      </span>
                    </div>
                    {getQualityControlLevel(position.komplexität) <= 2 ? (
                      <RangeIndicator 
                        level={getQualityControlLevel(position.komplexität) + 1} 
                        maxLevel={3} 
                        labels={['Sehr einfach', 'Geringer Aufwand', 'Hoher Aufwand']} 
                      />
                    ) : (
                      <div className="flex items-center">
                        <div className="flex-grow">
                          <RangeIndicator 
                            level={3} 
                            maxLevel={3} 
                            labels={['Sehr einfach', 'Geringer Aufwand', 'Hoher Aufwand']} 
                          />
                        </div>
                        <div className="ml-3 flex items-center text-xs">
                          <span className="text-gray-500 mr-1">→</span>
                          <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded font-medium">
                            {LEVEL_LABELS.complexity.qualityControl[getQualityControlLevel(position.komplexität)]}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Debug section - show raw data structure */}
              {import.meta.env.DEV && (
                <div className="col-span-full bg-gray-100 border border-gray-300 rounded-lg overflow-hidden">
                  <button 
                    onClick={() => setIsDebugOpen(!isDebugOpen)}
                    className="w-full p-4 hover:bg-gray-200 transition-colors duration-200 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center">
                      <span className="text-xl mr-2">🔍</span>
                      <h4 className="font-semibold text-gray-900">Debug: Raw Data</h4>
                    </div>
                    <svg 
                      className={`w-5 h-5 transition-transform duration-200 ${isDebugOpen ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isDebugOpen && (
                    <div className="p-4 bg-gray-50 border-t border-gray-300">
                      <div className="text-xs text-gray-600 space-y-2">
                        <div>
                          <strong>Kommunikation array:</strong>
                          <pre className="mt-1 text-xs bg-white p-2 rounded">
                            {JSON.stringify(position.kommunikation, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <strong>Komplexität array:</strong>
                          <pre className="mt-1 text-xs bg-white p-2 rounded">
                            {JSON.stringify(position.komplexität, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <strong>Calculated levels:</strong>
                          <pre className="mt-1 text-xs bg-white p-2 rounded">
                            {JSON.stringify({
                              communicationFrequency: getCommunicationFrequencyLevel(position.kommunikation),
                              communicationStyle: getCommunicationStyleLevel(position.kommunikation),
                              strategicInfluence: getStrategicInfluenceLevel(position.komplexität),
                              resultVisibility: getResultVisibilityLevel(position.komplexität),
                              qualityControlLevel: getQualityControlLevel(position.komplexität),
                              qualityControlDescription: getQualityControlDescription(position.komplexität)
                            }, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Benchmarking Sources */}
      <div className="p-6">
        <h4 className="text-lg font-semibold mb-4">📊 Markt-Benchmarking</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Compensation Partner */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-semibold text-green-800">Compensation Partner</h5>
              <div className="flex items-center gap-2">
                <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">Primärquelle</span>
                {hasManagementResponsibility && (
                  <div className="relative group">
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex items-center cursor-help">
                      <span className="mr-1">👥</span>
                      <span>✓</span>
                    </span>
                    <div className="absolute bottom-full right-0 mb-2 p-2 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                      <div className="font-semibold mb-1">Führungsverantwortung berücksichtigt</div>
                      <div className="text-gray-300">
                        CP-Daten enthalten separate Benchmarks<br/>
                        für Ihre Führungsposition
                      </div>
                      <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Markt-Median (Total Cash):</span>
                <span className="font-semibold">{position.salary.medianTotalCash.toLocaleString()} €/Jahr</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Monatlich:</span>
                <span className="font-semibold">{cpMonthly.toLocaleString()} €</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Abweichung:</span>
                <span className={`font-semibold ${
                  position.salary.medianTotalCash > position.salary.peerigonCurrentTotalCash ? 'text-red-600' : 
                  position.salary.medianTotalCash < position.salary.peerigonCurrentTotalCash ? 'text-green-600' : 
                  'text-gray-600'
                }`}>
                  {position.salary.medianTotalCash !== position.salary.peerigonCurrentTotalCash && 
                    (position.salary.medianTotalCash > position.salary.peerigonCurrentTotalCash ? '+' : '')
                  }
                  {Math.round(((position.salary.medianTotalCash - position.salary.peerigonCurrentTotalCash) / position.salary.peerigonCurrentTotalCash) * 100)}%
                </span>
              </div>
              
              <div className="pt-2 mt-2 border-t border-green-200">
                <div className="text-xs text-gray-600">
                  <div>• {stellenData.length || 1} Stellenprofile</div>
                  {stellenData && stellenData.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {stellenData.map((stelle, index) => (
                        <div key={index} className="ml-3 text-green-700 text-xs">
                          - {stelle?.selectJob || 'Unbekannte Stelle'}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-1">• Region: Bundesweit</div>
                  <div>• Branche: Branchenunabhängig</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Kununu */}
          <div className={`${position.kununu ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 border`}>
            <div className="flex items-center justify-between mb-3">
              <h5 className={`font-semibold ${position.kununu ? 'text-blue-800' : 'text-gray-400'}`}>Kununu</h5>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded ${position.kununu ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-600'}`}>
                  Community
                </span>
                {position.kununu && hasManagementResponsibility && (
                  <div className="relative group">
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center cursor-help">
                      <span className="mr-1">👥</span>
                      <span>{position.kununu.config?.hasManagement ? '✓' : '✗'}</span>
                    </span>
                    <div className="absolute bottom-full right-0 mb-2 p-2 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                      <div className="font-semibold mb-1">
                        {position.kununu.config?.hasManagement ? 'Führungsdaten verwendet' : 'Führungsdaten fehlen'}
                      </div>
                      <div className="text-gray-300">
                        {position.kununu.config?.hasManagement 
                          ? <>Die Gehaltsdaten berücksichtigen<br/>Ihre Führungsverantwortung</> 
                          : <>Kununu hat für diese Position keine<br/>separaten Führungsdaten verfügbar</>}
                      </div>
                      <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {position.kununu ? (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Durchschnitt:</span>
                  <span className="font-semibold">{position.kununu.averageSalary.toLocaleString()} €/Jahr</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Monatlich:</span>
                  <span className="font-semibold">{kununuMonthly.toLocaleString()} €</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Abweichung:</span>
                  <span className={`font-semibold ${
                    position.kununu.averageSalary > position.salary.peerigonCurrentTotalCash ? 'text-red-600' : 
                    position.kununu.averageSalary < position.salary.peerigonCurrentTotalCash ? 'text-green-600' : 
                    'text-gray-600'
                  }`}>
                    {position.kununu.averageSalary !== position.salary.peerigonCurrentTotalCash && 
                      (position.kununu.averageSalary > position.salary.peerigonCurrentTotalCash ? '+' : '')
                    }
                    {Math.round(((position.kununu.averageSalary - position.salary.peerigonCurrentTotalCash) / position.salary.peerigonCurrentTotalCash) * 100)}%
                  </span>
                </div>
                
                <div className="pt-2 mt-2 border-t border-blue-200">
                  <div className="text-xs text-gray-600">
                    <div>• Position: <a 
                      href={position.kununu.config.kununuUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {position.kununu.position}
                    </a></div>
                    <div>• {position.kununu.dataPoints} Datenpunkte</div>
                    <div>• Level: {position.kununu.config.experienceLevel}</div>
                    <div>• Führung: {position.kununu.config.hasManagement ? 'Ja' : 'Nein'}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <p className="text-sm">Keine Daten verfügbar</p>
              </div>
            )}
          </div>
          
          {/* Gehalt.de */}
          <div className={`${position.gehaltDe ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 border`}>
            <div className="flex items-center justify-between mb-3">
              <h5 className={`font-semibold ${position.gehaltDe ? 'text-orange-800' : 'text-gray-400'}`}>Gehalt.de</h5>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded ${position.gehaltDe ? 'bg-orange-200 text-orange-800' : 'bg-gray-200 text-gray-600'}`}>
                  Marktdaten
                </span>
                {position.gehaltDe && hasManagementResponsibility && (
                  <div className="relative group">
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded flex items-center cursor-help">
                      <span className="mr-1">👥</span>
                      <span>✗</span>
                    </span>
                    <div className="absolute bottom-full right-0 mb-2 p-2 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                      <div className="font-semibold mb-1">Führungsdaten nicht verfügbar</div>
                      <div className="text-gray-300">
                        Gehalt.de kann Ihre Führungsposition<br/>
                        nicht separat berücksichtigen<br/>
                        (nur allgemeine Erfahrungsstufen)
                      </div>
                      <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {position.gehaltDe ? (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Durchschnitt:</span>
                  <span className="font-semibold">{position.gehaltDe.averageSalary.toLocaleString()} €/Jahr</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Monatlich:</span>
                  <span className="font-semibold">{gehaltDeMonthly.toLocaleString()} €</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Abweichung:</span>
                  <span className={`font-semibold ${
                    position.gehaltDe.averageSalary > position.salary.peerigonCurrentTotalCash ? 'text-red-600' : 
                    position.gehaltDe.averageSalary < position.salary.peerigonCurrentTotalCash ? 'text-green-600' : 
                    'text-gray-600'
                  }`}>
                    {position.gehaltDe.averageSalary ? 
                      `${position.gehaltDe.averageSalary > position.salary.peerigonCurrentTotalCash ? '+' : ''}${Math.round(((position.gehaltDe.averageSalary - position.salary.peerigonCurrentTotalCash) / position.salary.peerigonCurrentTotalCash) * 100)}%` 
                      : '-'
                    }
                  </span>
                </div>
                
                <div className="pt-2 mt-2 border-t border-orange-200">
                  <div className="text-xs text-gray-600">
                    <div>• Position: <a 
                      href={position.gehaltDe.config.gehaltDeUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-orange-600 hover:text-orange-800 hover:underline"
                    >
                      {position.gehaltDe.position}
                    </a></div>
                    <div>• Level: {position.gehaltDe.config.experienceLevel}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <p className="text-sm">Keine Daten verfügbar</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Legend for indicators - only show if position has management responsibility */}
        {hasManagementResponsibility && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-center gap-6 text-xs">
              <div className="flex items-center gap-2">
                <span className="flex items-center bg-gray-100 px-2 py-1 rounded">
                  <span className="mr-1">👥</span>
                  <span>✓</span>
                </span>
                <span className="text-gray-600">Führungsverantwortung berücksichtigt</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center bg-gray-100 px-2 py-1 rounded">
                  <span className="mr-1">👥</span>
                  <span>✗</span>
                </span>
                <span className="text-gray-600">Führungsverantwortung nicht berücksichtigt</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Recommendation Section */}
        <div className="mt-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-6 border border-indigo-200">
          <h4 className="text-lg font-semibold mb-4 text-indigo-800">💡 Empfehlung</h4>
          
          {allAvailableAdjustments.length > 0 ? (
            <>
              {/* Show all available adjustments with integrated checkboxes */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {allAvailableAdjustments.map((adj, idx) => {
                  const isIncluded = sourceInclusion[adj.key];
                  return (
                    <label 
                      key={idx} 
                      className={`cursor-pointer rounded p-3 text-center border-2 transition-all duration-200 ${
                        isIncluded 
                          ? 'bg-white border-indigo-200 hover:border-indigo-300' 
                          : 'bg-gray-50 border-gray-200 opacity-60 hover:opacity-80'
                      }`}
                    >
                      {/* Checkbox in top-right corner */}
                      <div className="flex justify-between items-start mb-2">
                        <div className={`text-sm font-medium ${isIncluded ? 'text-gray-600' : 'text-gray-400'}`}>
                          {adj.source}
                        </div>
                        <input
                          type="checkbox"
                          checked={isIncluded}
                          onChange={() => handleSourceToggle(adj.key)}
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 scale-110"
                        />
                      </div>
                      
                      <div className={`text-xl font-bold ${
                        isIncluded 
                          ? (adj.amount >= 0 ? 'text-red-600' : 'text-green-600')
                          : 'text-gray-400'
                      }`}>
                        {adj.amount >= 0 ? '+' : ''}{adj.amount} €
                      </div>
                      <div className={`text-xs ${isIncluded ? 'text-gray-500' : 'text-gray-400'}`}>
                        monatlich
                      </div>
                      
                      {/* Show percentage for this source */}
                      <div className={`text-xs font-medium ${
                        isIncluded 
                          ? (adj.amount >= 0 ? 'text-red-500' : 'text-green-500')
                          : 'text-gray-400'
                      }`}>
                        {(() => {
                          let percentage = 0;
                          const currentTotalCash = position?.salary?.peerigonCurrentTotalCash || 0;
                          
                          if (adj.key === 'compensationPartner') {
                            // Use the same calculation logic as other sources for consistency
                            const cpMedian = position?.salary?.medianTotalCash || 0;
                            percentage = ((cpMedian - currentTotalCash) / currentTotalCash) * 100;
                          } else if (adj.key === 'kununu' && position?.kununu?.averageSalary) {
                            percentage = ((position.kununu.averageSalary - currentTotalCash) / currentTotalCash) * 100;
                          } else if (adj.key === 'gehaltDe' && position?.gehaltDe?.averageSalary) {
                            percentage = ((position.gehaltDe.averageSalary - currentTotalCash) / currentTotalCash) * 100;
                          }
                          return `${percentage >= 0 ? '+' : ''}${Math.round(percentage)}%`;
                        })()}
                      </div>
                      
                      {/* Visual indicator for included/excluded */}
                      <div className={`mt-2 text-xs font-medium ${
                        isIncluded ? 'text-indigo-600' : 'text-gray-400'
                      }`}>
                        {isIncluded ? '✓ Einbezogen' : '✗ Ausgeschlossen'}
                      </div>
                    </label>
                  );
                })}
              </div>
              
              {/* Average recommendation based on selected sources */}
              {allAdjustments.length > 0 ? (
                <div className="bg-white rounded p-4 text-center">
                  <div className="text-sm text-gray-600 mb-1">
                    Durchschnittliche Empfehlung ({allAdjustments.length} von {allAvailableAdjustments.length} Quellen)
                  </div>
                  <div className={`text-2xl font-bold ${averageAdjustment >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {averageAdjustment >= 0 ? '+' : ''}{averageAdjustment} € / Monat
                  </div>
                  <div className={`text-lg mt-1 ${averageAdjustment >= 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {averageAdjustment >= 0 ? '+' : ''}{averageAdjustment * 12} € / Jahr
                  </div>
                  
                  {/* Show average percentage */}
                  <div className={`text-sm font-medium mt-2 ${averageAdjustment >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {(() => {
                      // Calculate average percentage from included sources
                      const currentTotalCash = position?.salary?.peerigonCurrentTotalCash || 0;
                      const percentages = allAdjustments.map(adj => {
                        if (adj.key === 'compensationPartner') {
                          // Use the same calculation logic as other sources for consistency
                          const cpMedian = position?.salary?.medianTotalCash || 0;
                          return ((cpMedian - currentTotalCash) / currentTotalCash) * 100;
                        } else if (adj.key === 'kununu' && position?.kununu?.averageSalary) {
                          return ((position.kununu.averageSalary - currentTotalCash) / currentTotalCash) * 100;
                        } else if (adj.key === 'gehaltDe' && position?.gehaltDe?.averageSalary) {
                          return ((position.gehaltDe.averageSalary - currentTotalCash) / currentTotalCash) * 100;
                        }
                        return 0;
                      });
                      
                      const avgPercentage = percentages.length > 0 
                        ? percentages.reduce((sum, p) => sum + p, 0) / percentages.length 
                        : 0;
                      
                      return `${avgPercentage >= 0 ? '+' : ''}${Math.round(avgPercentage)}% vs. aktuell`;
                    })()}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-100 rounded p-4 text-center">
                  <div className="text-sm text-gray-500 mb-1">Keine Quellen ausgewählt</div>
                  <div className="text-lg text-gray-400">Bitte wählen Sie mindestens eine Quelle aus</div>
                </div>
              )}
              
              <p className="text-sm text-gray-600 mt-4">
                {allAdjustments.length > 0 ? (
                  <>
                    Durchschnitt aus {allAdjustments.length} ausgewählte{allAdjustments.length > 1 ? 'n' : ''} Datenquelle{allAdjustments.length > 1 ? 'n' : ''} 
                    (einschließlich negativer Anpassungen).
                  </>
                ) : (
                  'Wählen Sie oben Datenquellen aus, um eine Empfehlung zu erhalten.'
                )}
              </p>
              
              {/* Show additional context if there are mixed results */}
              {allAdjustments.length > 0 && positiveAdjustments.length > 0 && positiveAdjustments.length < allAdjustments.length && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    ⚠️ Gemischte Empfehlungen: {positiveAdjustments.length} Quelle{positiveAdjustments.length > 1 ? 'n' : ''} empfehlen eine Erhöhung, 
                    {allAdjustments.length - positiveAdjustments.length} eine Reduktion.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center">
              <div className="text-2xl mb-2">❓</div>
              <p className="text-lg font-semibold text-gray-700 mb-2">Keine Marktdaten verfügbar</p>
              <p className="text-sm text-gray-600">
                Es sind keine ausreichenden Markt-Benchmarks verfügbar, um eine Empfehlung zu geben.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(PositionDetails);